import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, Landmark, TowerControl, ChevronRight } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import IAAboutArticle from "../components/india/IAAboutArticle";
import IAOciNriArticle from "../components/india/IAOciNriArticle";
import TripAutoHero from "../components/TripAutoHero";
import api from "../lib/api";

const INFOGRAPHIC =
  "https://customer-assets-rejwkqb3.emergentagent.net/job_flight-network-1/artifacts/kbzt284e_infographic-add-city-profiles.png";

/* IndianAirports.com — About page.
   Tab 1: platform infographic (full article coming from the owner).
   Tab 2: City Profiles — every airport city with links to its airport portal + state page. */
export default function IndiaAbout() {
  const [airports, setAirports] = useState([]);
  const [stateSlugs, setStateSlugs] = useState({});

  useEffect(() => {
    document.title = "About — IndianAirports.com";
    api.get("/india/airports").then((r) => setAirports(r.data.items || []));
    api.get("/india/airports/states").then((r) => {
      const map = {};
      (r.data.states || r.data || []).forEach((s) => { map[s.state] = s.slug; });
      setStateSlugs(map);
    });
    window.scrollTo(0, 0);
  }, []);

  // One card per city (a city can host multiple airports, e.g. future Navi Mumbai)
  const cities = {};
  airports.forEach((a) => {
    (cities[a.city] = cities[a.city] || { city: a.city, state: a.state, airports: [] }).airports.push(a);
  });
  const cityList = Object.values(cities).sort((x, y) => x.city.localeCompare(y.city));

  return (
    <div data-testid="india-about-page">
      <div className="relative border-b border-zinc-200 overflow-hidden">
        <TripAutoHero query="India airport terminal aviation" alt="Indian aviation network" className="absolute inset-0 w-full h-full" testid="india-about-hero" />
        <div className="absolute inset-0 bg-gradient-to-tr from-blue-900/85 via-blue-700/70 to-orange-600/55" />
        <div className="relative max-w-5xl mx-auto px-5 py-14">
          <p className="text-xs font-mono uppercase tracking-widest text-white/85">IndianAirports.com · About</p>
          <h1 className="mt-2 font-heading font-900 text-4xl sm:text-5xl tracking-tight text-white" data-testid="india-about-title">
            About the platform
          </h1>
          <p className="mt-3 text-base text-white/90 max-w-2xl">
            Every Airport. Every Journey. One India aviation platform — airport portals, city profiles,
            routes, airlines and traveler intelligence for all of India.
          </p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-5 py-8">
        <Tabs defaultValue="about" data-testid="india-about-tabs">
          <TabsList className="mb-6">
            <TabsTrigger value="about" data-testid="india-about-tab-about">About</TabsTrigger>
            <TabsTrigger value="cities" data-testid="india-about-tab-cities">City Profiles</TabsTrigger>
            <TabsTrigger value="oci" data-testid="india-about-tab-oci">OCI & NRI Rules</TabsTrigger>
          </TabsList>

          <TabsContent value="about" data-testid="india-about-content">
            <div className="bg-white border border-zinc-200 rounded-2xl overflow-hidden">
              <img
                src={INFOGRAPHIC}
                alt="IndianAirports.com — how the platform works, from airport profiles to city intelligence"
                className="w-full h-auto"
                data-testid="india-about-infographic"
              />
            </div>
            <div className="mt-8">
              <IAAboutArticle />
            </div>
          </TabsContent>

          <TabsContent value="cities" data-testid="india-cities-content">
            <p className="text-sm text-zinc-600 mb-5">
              {cityList.length} airport cities across India — each with a dedicated airport portal and a state landing page.
            </p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="india-city-grid">
              {cityList.map((c) => (
                <div key={c.city} className="bg-white border border-zinc-200 rounded-2xl p-4 hover:border-blue-300 transition-colors" data-testid={`india-city-card-${c.city.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                  <div className="flex items-center gap-1.5">
                    <MapPin className="w-4 h-4 text-orange-600 shrink-0" />
                    <span className="font-800 text-sm">{c.city}</span>
                    <span className="ml-auto text-[10px] font-700 text-zinc-400 uppercase">{c.state}</span>
                  </div>
                  <div className="mt-2.5 space-y-1.5">
                    {c.airports.map((a) => (
                      <Link key={a.iata} to={`/india/airports/${a.slug}`} data-testid={`india-city-airport-link-${a.iata}`}
                        className="group flex items-center gap-1.5 text-xs text-zinc-600 hover:text-blue-700">
                        <TowerControl className="w-3.5 h-3.5 text-blue-600 shrink-0" />
                        <span className="truncate">{a.name} <b className="font-mono text-[10px]">{a.iata}</b></span>
                        <ChevronRight className="w-3 h-3 ml-auto shrink-0 opacity-0 group-hover:opacity-100" />
                      </Link>
                    ))}
                    {stateSlugs[c.state] && (
                      <Link to={`/india/airports/states/${stateSlugs[c.state]}`} data-testid={`india-city-state-link-${c.city.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                        className="group flex items-center gap-1.5 text-xs text-zinc-500 hover:text-orange-600">
                        <Landmark className="w-3.5 h-3.5 text-orange-500 shrink-0" />
                        <span>Airports in {c.state}</span>
                        <ChevronRight className="w-3 h-3 ml-auto shrink-0 opacity-0 group-hover:opacity-100" />
                      </Link>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="oci" data-testid="india-oci-content">
            <p className="mb-4 text-xs text-zinc-500 bg-blue-50 border border-blue-200 rounded-xl px-4 py-2.5">
              This guide also lives at its own shareable page:{" "}
              <Link to="/india/guides/oci-nri-rules" className="font-700 text-blue-700 hover:underline" data-testid="india-oci-permalink">
                indianairports · OCI & NRI Rules guide
              </Link>
            </p>
            <IAOciNriArticle />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
